import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Clock, PackagePlus, CreditCard, TrendingUp, Wallet, History } from "lucide-react";
import { toast } from "sonner";

/**
 * Widget "Ricarica ore" per tecnici distributori.
 * Mostra saldo carnet corrente, storico ultimi acquisti, e pulsanti per ricaricare.
 */
export function RicaricaOreWidget() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [my, hist] = await Promise.all([
        api.get("/my-carnet"),
        api.get("/my-carnet/history"),
      ]);
      setData(my.data);
      setHistory(hist.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buy = async (pkgKey) => {
    setBusy(pkgKey);
    try {
      const { data: resp } = await api.post("/payments/hours/checkout", {
        package: pkgKey,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="ricarica-ore-widget-loading">
        <Loader2 className="h-5 w-5 animate-spin text-brand" />
      </Card>
    );
  }

  const pricing = data?.pricing || {};
  const carnet = data?.carnet;
  const remaining = carnet?.hours_remaining ?? 0;
  const total = carnet?.hours_total ?? 0;
  const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
  const low = remaining < 2;
  const purchasesCount = history.filter(h => h.payment_status === "paid").length;

  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="ricarica-ore-widget">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="h-4 w-4 text-brand" strokeWidth={1.75} />
        <div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-950">Ore assistenza</h3>
          <p className="text-xs text-zinc-500">Saldo carnet + ricarica rapida</p>
        </div>
      </div>

      {/* Saldo attuale */}
      <div className={`rounded-sm p-4 mb-4 ${low ? "bg-amber-50 border border-amber-200" : "bg-brand-muted/30 border border-brand/20"}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Ore disponibili</div>
            <div className="flex items-baseline gap-2">
              <div className={`font-display text-4xl font-bold tracking-tighter ${low ? "text-amber-800" : "text-zinc-950"}`} data-testid="ricarica-ore-remaining">
                {remaining}
              </div>
              <div className="text-sm text-zinc-500">/ {total} ore</div>
            </div>
            {low && <div className="text-xs text-amber-800 mt-1 font-medium">⚠️ Saldo basso — ricarica presto</div>}
          </div>
          {carnet && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Consumo</div>
              <div className={`text-2xl font-semibold ${low ? "text-amber-700" : "text-brand"}`}>{100 - pct}%</div>
            </div>
          )}
        </div>
        {carnet && (
          <div className="mt-3 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${low ? "bg-amber-500" : "bg-brand"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>

      {/* CTA ricarica */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => buy("single_hour")}
          disabled={busy !== null}
          data-testid="ricarica-ore-single-btn"
          className="text-left rounded-sm border border-zinc-200 p-4 hover:border-brand hover:bg-brand-muted/30 transition disabled:opacity-50"
        >
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-3.5 w-3.5 text-brand" />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ora singola</div>
          </div>
          <div className="font-display text-2xl font-bold text-zinc-950">1 ora</div>
          <div className="text-lg font-semibold text-brand">€{(pricing.hourly_rate || 0).toFixed(2)}</div>
          <div className="text-xs text-brand mt-2 flex items-center gap-1">
            {busy === "single_hour" ? <><Loader2 className="h-3 w-3 animate-spin" /> Redirect...</> : <><CreditCard className="h-3 w-3" /> Ricarica</>}
          </div>
        </button>

        <button
          onClick={() => buy("carnet_10h")}
          disabled={busy !== null}
          data-testid="ricarica-ore-carnet-btn"
          className="text-left rounded-sm border-2 border-brand p-4 bg-brand-muted/40 hover:bg-brand-muted transition disabled:opacity-50"
        >
          <div className="flex items-center gap-2 mb-1">
            <PackagePlus className="h-3.5 w-3.5 text-brand" />
            <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Carnet · Consigliato</div>
          </div>
          <div className="font-display text-2xl font-bold text-zinc-950">10 ore</div>
          <div className="text-lg font-semibold text-brand">€{(pricing.carnet_10h_price || 0).toFixed(2)}</div>
          {pricing.hourly_rate > 0 && pricing.carnet_10h_price > 0 && (
            <div className="text-[10px] text-emerald-700 mt-2 flex items-center gap-1 font-medium">
              <TrendingUp className="h-3 w-3" /> Risparmi €{(pricing.hourly_rate * 10 - pricing.carnet_10h_price).toFixed(2)}
            </div>
          )}
        </button>
      </div>

      {/* Storico */}
      {history.length > 0 && (
        <details className="mt-5 pt-4 border-t border-zinc-100" data-testid="ricarica-ore-history">
          <summary className="cursor-pointer text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2 hover:text-brand">
            <History className="h-3 w-3" /> Storico acquisti ({purchasesCount} completati)
          </summary>
          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
            {history.slice(0, 10).map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-2 text-xs py-2 border-b border-zinc-100 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-900 font-medium">
                    {h.package === "single_hour" ? "1 ora" : `Carnet ${h.hours}h`}
                  </div>
                  <div className="text-zinc-500">{new Date(h.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <div className="text-right">
                  <div className="text-zinc-900 font-semibold">€{Number(h.amount).toFixed(2)}</div>
                  <div className={`text-[10px] uppercase tracking-widest ${h.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>
                    {h.payment_status === "paid" ? "Pagato" : h.payment_status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}
